import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Group,
  Loader,
  NumberInput,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { FEATURE_PRICING, useFeatures } from '../auth/useFeatures';
import {
  fractionToPercent,
  listItemPriceTiers,
  percentToFraction,
  setItemPriceTiers,
} from './api';

interface Props {
  /** Null while creating — an item has to exist before it can have tiers. */
  itemId: string | null;
  /** The item's stocking unit, which is what every boundary is measured in. */
  stockingUomCode: string | null;
}

interface Row {
  minQuantity: number | '';
  /** Held as a PERCENTAGE here, because that is what a person types. */
  percent: number | '';
}

const TIERS_KEY = 'item-price-tiers';

/**
 * An item's volume price tiers.
 *
 * **These are the default pricing for every customer.** legacy's PriceMethod defaults to 1, which
 * is exactly this table, so a tier typed here prices every line for everybody nobody has
 * configured otherwise. It is not an opt-in feature behind a customer flag.
 *
 * **The quantity column is labelled with the item's STOCKING unit**, because that is what the
 * boundary is measured in however a line is entered: 100 loose eaches of a case-stocked item is
 * 8.33 and reaches nothing. Legacy has the same rule and shows a bare "10", which is where the
 * surprise gets discovered — on an invoice.
 *
 * **The discount is typed as a percentage and stored as a fraction.** One conversion, in api.ts,
 * because a mistake in either direction is a hundredfold pricing error.
 *
 * **Five rows are not a limit.** legacy has exactly five columns and a matching CASE with two
 * silent gaps in it — leave tier 1 blank and nothing is ever discounted. This is a child table
 * with no such trap, and the grid adds rows as they are needed.
 */
export function ItemPriceTiers({ itemId, stockingUomCode }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { has } = usePermissions();

  const features = useFeatures();

  // Two different questions, and both have to answer yes. The add-on says the tenant
  // bought Pricing; the permission says this user may see the structure. The server
  // enforces both independently -- this just avoids showing a screen that would 403.
  const canRead = features.has(FEATURE_PRICING) && has('pricing:read');
  // No add-on check here: canRead above already requires it and the component renders
  // nothing without it, so repeating it would be a condition no test can reach.
  const canWrite = has('pricing:write');

  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: [TIERS_KEY, itemId] as const,
    queryFn: ({ signal }) => listItemPriceTiers(itemId!, signal),
    enabled: Boolean(itemId) && canRead,
  });

  useEffect(() => {
    if (!query.data) return;
    setRows(
      query.data.map((tier) => ({
        minQuantity: tier.minQuantity,
        percent: fractionToPercent(tier.discount),
      })),
    );
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () =>
      setItemPriceTiers(
        itemId!,
        rows
          .filter((r) => r.minQuantity !== '' && r.percent !== '')
          .map((r) => ({
            minQuantity: Number(r.minQuantity),
            discount: percentToFraction(Number(r.percent)),
          })),
      ),
    onSuccess: (saved) => {
      queryClient.setQueryData([TIERS_KEY, itemId], saved);
      setError(null);
      notifications.show({
        color: 'teal',
        message: t('pricing.tiers.saved'),
      });
    },
    onError: (err) => {
      setError(
        err instanceof ApiError && err.problem.detail
          ? err.problem.detail
          : t('pricing.tiers.saveFailed'),
      );
    },
  });

  if (!canRead) return null;

  // Tiers belong to an item, so there is nothing to show until one exists. Saying so beats an
  // empty grid that silently discards what was typed into it.
  if (!itemId) {
    return (
      <Card withBorder padding="sm">
        <Text size="sm" c="dimmed">
          {t('pricing.tiers.saveItemFirst')}
        </Text>
      </Card>
    );
  }

  const quantityLabel = stockingUomCode
    ? t('pricing.tiers.quantityIn', { uom: stockingUomCode })
    : t('pricing.tiers.quantity');

  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        {t('pricing.tiers.title')}
      </Text>
      <Text size="xs" c="dimmed" mb={4}>
        {t('pricing.tiers.subtitle')}
      </Text>

      {query.isLoading && <Loader size="sm" color="invetrak" />}

      {query.isError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {t('pricing.tiers.loadFailed')}
        </Alert>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      {query.isSuccess && (
        <Card withBorder padding="sm">
          <Stack gap="sm">
            {rows.length === 0 && (
              <Text size="sm" c="dimmed">
                {t('pricing.tiers.none')}
              </Text>
            )}

            {rows.length > 0 && (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{quantityLabel}</Table.Th>
                    <Table.Th>{t('pricing.tiers.discount')}</Table.Th>
                    {canWrite && <Table.Th w={48} />}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((row, index) => (
                    <Table.Tr key={index}>
                      <Table.Td>
                        <NumberInput
                          aria-label={t('pricing.tiers.quantityRow', { row: index + 1 })}
                          value={row.minQuantity}
                          onChange={(v) =>
                            setRows(
                              rows.map((r, i) =>
                                i === index ? { ...r, minQuantity: v === '' ? '' : Number(v) } : r,
                              ),
                            )
                          }
                          min={1}
                          // Whole units only, matching legacy's numeric(18,0) boundaries.
                          allowDecimal={false}
                          disabled={!canWrite}
                        />
                      </Table.Td>
                      <Table.Td>
                        <NumberInput
                          aria-label={t('pricing.tiers.discountRow', { row: index + 1 })}
                          value={row.percent}
                          onChange={(v) =>
                            setRows(
                              rows.map((r, i) =>
                                i === index ? { ...r, percent: v === '' ? '' : Number(v) } : r,
                              ),
                            )
                          }
                          min={0}
                          max={100}
                          suffix="%"
                          disabled={!canWrite}
                        />
                      </Table.Td>
                      {canWrite && (
                        <Table.Td>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={t('pricing.tiers.removeRow', { row: index + 1 })}
                            onClick={() => setRows(rows.filter((_, i) => i !== index))}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}

            {canWrite && (
              <Group gap="sm">
                <Button
                  size="xs"
                  variant="default"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setRows([...rows, { minQuantity: '', percent: '' }])}
                >
                  {t('pricing.tiers.add')}
                </Button>
                <Button
                  size="xs"
                  color="invetrak"
                  loading={mutation.isPending}
                  onClick={() => mutation.mutate()}
                >
                  {t('pricing.tiers.save')}
                </Button>
              </Group>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
